import React from 'react';
import { Shield, Lock, Eye, Database, UserCheck, Mail, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  const sections = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Information We Collect",
      content: [
        {
          subtitle: "Personal Information",
          text: "We collect personal information that you provide to us directly, including but not limited to: full name, email address, phone number, mailing address, project details, and any other information you choose to provide when requesting quotes, making inquiries, or using our services."
        },
        {
          subtitle: "Automatically Collected Information",
          text: "When you visit our website, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies installed on your device. We also collect information about how you interact with our website."
        },
        {
          subtitle: "Project Information",
          text: "When you engage our services, we collect information related to your construction project, including property details, budget information, timeline requirements, and specific project preferences."
        }
      ]
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "How We Use Your Information",
      content: [
        {
          subtitle: "Service Delivery",
          text: "We use your information to provide, maintain, and improve our construction and real estate services, process your requests, communicate with you about your projects, and deliver the services you've requested."
        },
        {
          subtitle: "Communication",
          text: "We use your contact information to send you updates about your projects, respond to your inquiries, provide customer support, and send you technical notices, security alerts, and administrative messages."
        },
        {
          subtitle: "Business Operations",
          text: "We use collected information to understand how our services are used, develop new services and features, conduct research and analysis, and improve our business operations."
        },
        {
          subtitle: "Marketing",
          text: "With your consent, we may use your information to send you marketing communications about our services, promotions, and industry insights. You can opt-out of marketing communications at any time."
        }
      ]
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "How We Protect Your Information",
      content: [
        {
          subtitle: "Security Measures",
          text: "We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, and regular security assessments."
        },
        {
          subtitle: "Access Controls",
          text: "We restrict access to personal information to employees, contractors, and agents who need to know that information to process it for us and who are subject to strict contractual confidentiality obligations."
        },
        {
          subtitle: "Data Retention",
          text: "We retain your personal information only for as long as necessary to fulfill the purposes for which we collected it, including legal, accounting, or reporting requirements."
        }
      ]
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Information Sharing",
      content: [
        {
          subtitle: "Service Providers",
          text: "We may share your information with third-party service providers who perform services on our behalf, such as payment processing, data analysis, email delivery, hosting services, and customer service."
        },
        {
          subtitle: "Legal Requirements",
          text: "We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders or government agencies)."
        },
        {
          subtitle: "Business Transfers",
          text: "In the event of a merger, acquisition, or sale of all or a portion of our assets, your personal information may be transferred as part of that transaction."
        },
        {
          subtitle: "With Your Consent",
          text: "We may share your information with third parties when we have your explicit consent to do so."
        }
      ]
    },
    {
      icon: <UserCheck className="w-8 h-8" />,
      title: "Your Rights",
      content: [
        {
          subtitle: "Access and Correction",
          text: "You have the right to access, update, or correct your personal information at any time. You can do this by contacting us directly."
        },
        {
          subtitle: "Data Portability",
          text: "You have the right to request a copy of your personal information in a structured, commonly used, and machine-readable format."
        },
        {
          subtitle: "Deletion",
          text: "You have the right to request deletion of your personal information, subject to certain legal obligations that may require us to retain certain information."
        },
        {
          subtitle: "Opt-Out",
          text: "You can opt-out of receiving marketing communications from us at any time by following the unsubscribe instructions in our emails or contacting us directly."
        },
        {
          subtitle: "Complaints",
          text: "If you have concerns about how we handle your personal information, you have the right to lodge a complaint with the relevant data protection authority."
        }
      ]
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Cookies and Tracking",
      content: [
        {
          subtitle: "What Are Cookies",
          text: "Cookies are small data files placed on your device when you visit our website. We use cookies to remember your preferences, understand how you use our site, and improve your experience."
        },
        {
          subtitle: "Types of Cookies We Use",
          text: "We use essential cookies (necessary for the website to function), analytical cookies (to understand how visitors use our site), and functional cookies (to remember your preferences)."
        },
        {
          subtitle: "Managing Cookies",
          text: "You can control and manage cookies through your browser settings. Please note that disabling cookies may affect the functionality of our website."
        }
      ]
    }
  ];

  return (
    <div className="bg-black text-white">
      <Navigation onQuoteClick={() => window.location.href = '/contact'} />

      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-600 rounded-full filter blur-3xl"></div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="w-20 h-20 bg-yellow-400/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-yellow-400" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
            Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
          </p>
          <p className="text-sm text-gray-400">
            Last Updated: January 20, 2026
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-600/10 border border-yellow-400/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              GUS Group Ltd ("we," "us," or "our") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with us.
            </p>
            <p className="text-gray-300 leading-relaxed">
              By using our services or providing us with your information, you agree to the terms of this Privacy Policy. If you do not agree with the terms of this policy, please do not access our website or use our services.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Sections */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {sections.map((section, index) => (
            <div key={index} className="scroll-mt-24" id={section.title.toLowerCase().replace(/\s+/g, '-')}>
              <div className="flex items-start gap-6 mb-8">
                <div className="w-16 h-16 bg-yellow-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <div className="text-yellow-400">
                    {section.icon}
                  </div>
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">
                    {section.title}
                  </h2>
                  <div className="w-20 h-1 bg-yellow-400"></div>
                </div>
              </div>

              <div className="space-y-6">
                {section.content.map((item, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-xl border border-yellow-400/10">
                    <h3 className="text-xl font-bold text-yellow-400 mb-3">
                      {item.subtitle}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Information */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {/* Children's Privacy */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Children's Privacy</h2>
              <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-xl border border-yellow-400/10">
                <p className="text-gray-300 leading-relaxed">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us, and we will delete such information from our systems.
                </p>
              </div>
            </div>

            {/* Third-Party Links */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Third-Party Links</h2>
              <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-xl border border-yellow-400/10">
                <p className="text-gray-300 leading-relaxed">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these third-party sites. We encourage you to read the privacy policies of any third-party sites you visit.
                </p>
              </div>
            </div>

            {/* Changes to Policy */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Changes to This Privacy Policy</h2>
              <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-xl border border-yellow-400/10">
                <p className="text-gray-300 leading-relaxed mb-4">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
                </p>
              </div>
            </div>

            {/* International Data Transfers */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">International Data Transfers</h2>
              <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-xl border border-yellow-400/10">
                <p className="text-gray-300 leading-relaxed">
                  Your information may be transferred to and processed in countries other than Ghana. These countries may have data protection laws that are different from the laws of your country. We take appropriate safeguards to ensure that your personal information remains protected in accordance with this Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border border-yellow-400/30 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Questions About Our Privacy Policy?</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-black/50 p-6 rounded-xl">
                <Mail className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-white font-bold mb-2">Email</h3>
                <a href="mailto:privacy@gusgroupltd.com" className="text-yellow-400 hover:text-yellow-500 transition-colors">
                  privacy@gusgroupltd.com
                </a>
              </div>

              <div className="bg-black/50 p-6 rounded-xl">
                <Shield className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-white font-bold mb-2">Phone</h3>
                <a href="tel:0592668258" className="text-yellow-400 hover:text-yellow-500 transition-colors">
                  0592668258
                </a>
              </div>

              <div className="bg-black/50 p-6 rounded-xl">
                <Mail className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-white font-bold mb-2">Address</h3>
                <p className="text-gray-400 text-sm">
                  Nii Afutu Tsuru St<br />Accra, Ghana
                </p>
              </div>
            </div>

            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-yellow-400 text-black px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105"
            >
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* WhatsApp Floating Button */}
      
      <a  href="https://wa.me/233592668258?text=Hello%20GUS%20Group,%20I%20have%20a%20question%20about%20your%20privacy%20policy"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all transform hover:scale-110 z-40"
      >
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default PrivacyPolicyPage;